import { useEffect, useState } from 'react';
import { ArrowRight, Camera, Database, ImagePlus, MapPin, Search, ShieldCheck, Sparkles, Upload, Zap } from 'lucide-react';
import { toast } from 'sonner';
import AdminPanel from './components/AdminPanel';
import Brand from './components/Brand';
import CameraCapture from './components/CameraCapture';
import ProcessingView from './components/ProcessingView';
import ResultsView from './components/ResultsView';
import { extractRestaurantText } from './lib/ocr';
import { findVisualMatches } from './lib/imageFingerprint';
import { getTextCandidateIds, rankRestaurants } from './lib/matching';
import { getReferenceManifest, loadRestaurantDirectory } from './lib/repository';

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function getRecentScans() {
  try {
    const saved = localStorage.getItem('scan-and-view-recents') || localStorage.getItem('menu-finder-recents') || '[]';
    return JSON.parse(saved);
  } catch { return []; }
}

export default function App() {
  const [path, setPath] = useState(window.location.pathname);
  const [screen, setScreen] = useState('home');
  const [restaurants, setRestaurants] = useState([]);
  const [source, setSource] = useState('loading');
  const [image, setImage] = useState('');
  const [detectedText, setDetectedText] = useState('');
  const [results, setResults] = useState([]);
  const [query, setQuery] = useState('');
  const [progress, setProgress] = useState(0);
  const [phase, setPhase] = useState('Preparing recognition engine');
  const [recents, setRecents] = useState(getRecentScans);

  useEffect(() => {
    const handler = () => setPath(window.location.pathname);
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  function navigate(nextPath) {
    window.history.pushState({}, '', nextPath);
    setPath(nextPath);
    window.scrollTo({ top: 0, behavior: 'auto' });
  }

  useEffect(() => {
    loadRestaurantDirectory().then(({ restaurants: items, source: loadedSource, error }) => {
      setRestaurants(items);
      setSource(loadedSource);
      if (error) toast.warning('Firebase is unavailable. Using the bundled restaurant directory.');
    });
  }, []);

  function remember(result) {
    if (!result) return;
    const next = [{ id: result.id, name: result.name, nameAr: result.nameAr, coverImage: result.coverImage, menuUrl: result.menuUrl, at: Date.now() }, ...recents.filter((item) => item.id !== result.id)].slice(0, 4);
    setRecents(next);
    localStorage.setItem('scan-and-view-recents', JSON.stringify(next));
  }

  async function analyzeImage(imageData) {
    if (!restaurants.length) return toast.error('The restaurant directory is still loading.');
    setImage(imageData);
    setDetectedText('');
    setResults([]);
    setProgress(0.03);
    setPhase('Preparing photo');
    setScreen('processing');

    try {
      const ocrPromise = extractRestaurantText(imageData, (value, status) => {
        setProgress(0.06 + value * 0.42);
        setPhase(status || 'Reading restaurant sign');
      }).catch((error) => {
        console.warn('OCR failed, continuing with visual matching', error);
        return { text: '', confidence: 0 };
      });

      const ocr = await ocrPromise;
      setDetectedText(ocr.text);
      setProgress(0.5);
      setPhase('Selecting visual candidates');
      const candidateIds = getTextCandidateIds(restaurants, ocr.text, ocr.confidence);
      const visualScores = await findVisualMatches(
        imageData,
        getReferenceManifest(),
        candidateIds,
        (value, status) => {
          setProgress(0.5 + value * 0.43);
          setPhase(status);
        },
      );
      setProgress(0.96);
      setPhase('Ranking final matches');
      const ranked = rankRestaurants(restaurants, ocr.text, visualScores);
      setResults(ranked);
      remember(ranked[0]);
      setTimeout(() => setScreen('results'), 180);
    } catch (error) {
      console.error(error);
      toast.error('The photo could not be analyzed.');
      setScreen('home');
    }
  }

  async function handleUpload(event) {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) return toast.error('Choose an image file.');
    if (file.size > 15 * 1024 * 1024) return toast.error('Please choose an image smaller than 15 MB.');
    await analyzeImage(await fileToDataUrl(file));
  }

  function manualSearch(event) {
    event.preventDefault();
    if (query.trim().length < 2) return toast.error('Type at least two letters.');
    const ranked = rankRestaurants(restaurants, query.trim(), {}, true);
    setImage('');
    setDetectedText(query.trim());
    setResults(ranked);
    remember(ranked[0]);
    setScreen('results');
  }

  function openMenu(restaurant) {
    if (!restaurant.menuUrl) return toast.error('This restaurant does not have a menu link yet.');
    window.open(restaurant.menuUrl, '_blank', 'noopener,noreferrer');
  }

  function reset() {
    setScreen('home'); setImage(''); setDetectedText(''); setResults([]); setProgress(0); setPhase('');
  }

  if (path === '/admin' || path.startsWith('/admin/')) return <AdminPanel onExit={() => navigate('/')} />;
  if (screen === 'camera') return <CameraCapture onCapture={analyzeImage} onClose={reset} onUpload={handleUpload} />;
  if (screen === 'processing') return <ProcessingView image={image} progress={progress} phase={phase} />;
  if (screen === 'results') return <ResultsView image={image} text={detectedText} results={results} onReset={reset} onOpen={openMenu} />;

  return (
    <div className="app-shell">
      <div className="orb orb-one" /><div className="orb orb-two" />
      <header className="home-header"><Brand /><div className="header-actions"><span className={`source-pill source-${source}`}><Database size={15} />{source === 'firebase' ? `${restaurants.length} live restaurants` : source === 'loading' ? 'Loading directory' : `${restaurants.length} local restaurants`}</span></div></header>
      <main>
        <section className="hero-section">
          <div className="hero-content">
            <span className="eyebrow"><Sparkles size={16} /> Restaurant sign recognition</span>
            <h1>Scan the restaurant.<br /><em>View the menu.</em></h1>
            <p>Point your camera at a restaurant sign. Scan &amp; View identifies the restaurant using its name and storefront, then opens the correct menu instantly.</p>
            <div className="hero-actions"><button className="primary-button large" onClick={() => setScreen('camera')} disabled={!restaurants.length}><Camera size={21} /> Scan restaurant <ArrowRight size={19} /></button><label className="secondary-button large"><Upload size={20} /> Upload photo<input hidden type="file" accept="image/*" onChange={handleUpload} /></label></div>
            <form className="hero-search" onSubmit={manualSearch}><Search /><input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Or type the restaurant name..." dir="auto" /><button><ArrowRight /></button></form>
            <div className="trust-row"><span><ShieldCheck /> No customer account</span><span><Zap /> Fast local image index</span><span><MapPin /> Arabic + English</span></div>
          </div>

        </section>

        <section className="workflow-section"><div className="section-heading"><div><span className="eyebrow">How recognition works</span><h2>Built for real storefront photos</h2></div><p>The app does not trust a single signal. It combines text, shape, color, edges, and several image crops before choosing a restaurant.</p></div><div className="workflow-grid"><article><span>01</span><Camera /><h3>Capture</h3><p>Take the storefront from any modern phone camera.</p></article><article><span>02</span><Search /><h3>Read + compare</h3><p>OCR and local image fingerprints analyze the sign together.</p></article><article><span>03</span><ArrowRight /><h3>Open menu</h3><p>High-confidence results lead directly to the restaurant menu.</p></article></div></section>

        {recents.length > 0 && <section className="recent-section"><div className="section-heading"><div><span className="eyebrow">On this device</span><h2>Recent discoveries</h2></div></div><div className="recent-grid">{recents.map((item) => <button key={item.id} onClick={() => openMenu(item)}>{item.coverImage ? <img src={item.coverImage} alt="" /> : <ImagePlus />}<span><strong>{item.name}</strong><small>{item.nameAr || 'Open menu'}</small></span><ArrowRight /></button>)}</div></section>}
      </main>
      <footer><span>Scan &amp; View</span><a href="https://hdalank.com" target="_blank" rel="noreferrer">developed by hdalank.com</a></footer>
    </div>
  );
}

